/**
 * Esperar a projeção alcançar o que acabou de ser criado.
 *
 * ── O defeito que isto conserta ───────────────────────────────────────────
 *
 * Criar oportunidade ou tarefa ESCREVE no motor. A lista que as mostra LÊ de
 * projeção — `read.opportunities` e `read.tasks` —, e a projeção só existe
 * depois de o evento do motor voltar pelo webhook e o worker processá-lo.
 *
 * As telas já invalidavam o cache no `then` da criação, e isso parecia certo.
 * Só que nesse instante a projeção ainda não tem nada: a rebusca chega antes do
 * evento e devolve a lista de antes. A tela fica igual, sem erro nenhum, e
 * quem criou conclui que não funcionou — até recarregar a página e ver que
 * funcionou o tempo todo.
 *
 * ── Por que esperar, e não mostrar otimista ───────────────────────────────
 *
 * Mostrar o card na hora, com o que o POST devolveu, seria mais imediato — e é
 * o que `mover.ts` faz ao mover de etapa, onde o desfecho é conhecido: o card
 * já existe e só muda de coluna.
 *
 * Na criação o desfecho não é conhecido. O card do funil carrega risco, última
 * interação e etapa desde, que são calculados na projeção e não vêm na
 * resposta da criação. Um card otimista nasceria diferente do que a projeção
 * vai trazer, e piscaria ao ser substituído. Pior: se o webhook estiver
 * desligado — que é exatamente o estado em que uma revenda recém-provisionada
 * nasce hoje —, o card apareceria e sumiria na rebusca seguinte, afirmando algo
 * que não aconteceu.
 *
 * Esperar é mais lento e não mente.
 *
 * ── O laço para sozinho, de três maneiras ─────────────────────────────────
 *
 * Quem decide que chegou é a própria lista: `esperando` volta a `false` quando
 * o dado aparece. É o mesmo desenho do `emCurso` do funil, onde quem larga o
 * movimento é o board ao mostrar o card no destino.
 *
 * Fora isso, o laço morre no TETO e na desmontagem. O teto importa: com a fila
 * presa nada vai chegar, e reperguntar para sempre transformaria um item que
 * não apareceu numa tela que consulta em looping até alguém sair dela.
 *
 * Seis segundos é generoso para um caminho que mede dezenas de milissegundos
 * quando está saudável. Quem passar disso tem problema de fila, não de tela —
 * e para esse caso existe o aviso de modo degradado.
 */

import { useEffect } from 'react';
import { useInvalidar } from './provedor';

/** Mesmo intervalo do laço de movimento do funil, pela mesma razão. */
const INTERVALO_MS = 1_200;

/** Cinco tentativas ≈ 6 s. Ver "o laço para sozinho". */
const TETO = 5;

/**
 * O laço, sem React: repete `repergunte` até o teto, e devolve como cancelar.
 *
 * Separado do hook porque é ele que carrega a regra — intervalo, teto, parada —
 * e regra se testa sem montar tela. O hook por cima é só ciclo de vida.
 */
export function iniciarEspera(repergunte: () => void): () => void {
  let tentativas = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;

  const agendar = (): void => {
    if (tentativas >= TETO) return;
    timer = setTimeout(() => {
      tentativas += 1;
      repergunte();
      agendar();
    }, INTERVALO_MS);
  };

  agendar();
  return () => clearTimeout(timer);
}

/**
 * Enquanto `esperando`, repergunta pelas `chaves` em intervalos curtos.
 *
 * As chaves são serializadas para a dependência do efeito porque um array novo
 * a cada render — que é o caso normal de `[chaves.funil()]` escrito em linha —
 * reiniciaria o laço a cada quadro, e o teto nunca seria alcançado.
 */
export function useEsperaDaProjecao(
  chaves: readonly (readonly unknown[])[],
  esperando: boolean,
): void {
  const invalidar = useInvalidar();
  const assinatura = JSON.stringify(chaves);

  useEffect(() => {
    if (!esperando) return;
    const lista = JSON.parse(assinatura) as readonly (readonly unknown[])[];
    return iniciarEspera(() => invalidar(lista));
  }, [esperando, assinatura, invalidar]);
}
